import { useEffect, useMemo, useRef } from "react";
import {
  CategoryScale,
  Chart,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  type ChartOptions,
  type ChartTypeRegistry,
  type Plugin,
  type ScriptableTooltipContext,
} from "chart.js";
import {
  getCurrentTimeSlot,
  REPEATED_HOUR_MARK,
  slotTimeLabel,
} from "../services/priceApi";
import { computePriceBreakdown } from "../utils/priceCalculations";
import { formatCoefficient, formatEuro } from "../utils/format";
import { COUNTRY_CONFIG } from "../config/countries";
import { STRINGS } from "../i18n";
import { Skeleton } from "./ui/skeleton";

Chart.register(
  CategoryScale,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

interface PriceChartProps {
  data: Record<string, number>;
  interval: "60m" | "15m";
  providerPerKwhCharge: number;
  providerSpotCoefficient?: number;
  includeTaxes: boolean;
  currentTime: Date;
  currentTimeLabel: string;
  regionId?: string | null;
}

export function PriceChart({
  data,
  interval,
  providerPerKwhCharge,
  providerSpotCoefficient,
  includeTaxes,
  currentTime,
  currentTimeLabel,
  regionId,
}: PriceChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart<"line"> | null>(null);
  // Dynamic values read by the canvas plugins at draw time. The plugins are
  // attached only once (when the chart is first created), so they must read
  // from this ref rather than closing over per-render values — otherwise
  // toggling the interval leaves the "Nu" line and min/max labels drawn from
  // the previous interval's data. See the drawStateRef update below.
  const drawStateRef = useRef({
    currentSlotIndex: -1,
    totalsLength: 0,
    minIndex: -1,
    maxIndex: -1,
    minTotal: 0,
    maxTotal: 0,
    lowHighlightIndices: [] as number[],
    highHighlightIndices: [] as number[],
    currentTimeLabel,
    highlightColor: "",
    destructiveColor: "",
    mutedFgColor: "",
    highColor: "",
    lowColor: "",
    nuColor: "",
  });

  const currentTimeSlot = useMemo(
    () => getCurrentTimeSlot(interval, currentTime),
    [interval, currentTime]
  );
  const energyTaxRate = useMemo(
    () => COUNTRY_CONFIG.getEnergyTaxRate(currentTime, regionId ?? undefined),
    [currentTime, regionId]
  );

  useEffect(() => {
    if (!canvasRef.current) return;

    const root = getComputedStyle(document.documentElement);
    const readVar = (name: string, fallback: string) =>
      (root.getPropertyValue(name).trim() || fallback) as string;
    const chartLineColor = readVar("--chart-1", "rgb(21, 94, 111)");
    const highlightColor = readVar("--muted", "#f8fafc");
    const destructiveColor = readVar("--destructive", "#ef4444");
    const mutedFgColor = readVar("--muted-foreground", "rgb(75, 85, 99)");
    // Highlight colors for the min/max/current-slot markers and tooltip border.
    const lowColor = readVar("--positive", "rgb(22, 163, 74)"); // green: lowest price
    const highColor = destructiveColor; // red: highest price
    const nuColor = readVar("--primary", "rgb(21, 94, 111)"); // current ("Nu") slot

    // Build a canonical full-day grid so the axis always runs 00:00–23:00 for
    // hourly data and 00:00–23:45 for 15-minute data, regardless of which
    // slots the API actually returned. Missing slots become gaps in the line;
    // the hour repeated when DST ends is inserted right after its first pass.
    const datePrefix = Object.keys(data)
      .sort((a, b) => a.localeCompare(b))[0]
      ?.slice(0, 10); // YYYY-MM-DD
    const slotKeys: string[] = [];
    if (datePrefix) {
      const minutes = interval === "60m" ? ["00"] : ["00", "15", "30", "45"];
      for (let hour = 0; hour < 24; hour++) {
        const hourKey = `${datePrefix}T${String(hour).padStart(2, "0")}`;
        const repeatedHourKey = `${hourKey}${REPEATED_HOUR_MARK}`;
        const hourKeys = Object.keys(data).some((key) =>
          key.startsWith(`${repeatedHourKey}:`)
        )
          ? [hourKey, repeatedHourKey]
          : [hourKey];
        for (const key of hourKeys) {
          for (const minute of minutes) {
            slotKeys.push(`${key}:${minute}`);
          }
        }
      }
    }
    const timeSlots: [string, number | undefined][] = slotKeys.map((key) => [
      key,
      data[key],
    ]);
    const isCurrentSlot = (timeKey: string) =>
      interval === "60m"
        ? timeKey.startsWith(currentTimeSlot)
        : timeKey === currentTimeSlot;

    const times = timeSlots.map(([time]) => slotTimeLabel(time));

    const breakdowns = timeSlots.map(([, spotPrice]) => {
      if (spotPrice === undefined) return null;
      const b = computePriceBreakdown({
        spotPrice,
        perKwhMarkup: providerPerKwhCharge,
        spotCoefficient: providerSpotCoefficient,
        energyTaxRate,
        vatRate: COUNTRY_CONFIG.vatRate,
        includeTaxes,
      });
      return {
        spotPrice,
        spotCoefficientSurcharge: b.spotCost - spotPrice,
        providerMarkup: b.markup,
        energyTax: b.energyTax,
        vat: b.vat,
      };
    });

    const currentSlotIndex = timeSlots.findIndex(([time]) =>
      isCurrentSlot(time)
    );
    const totals = breakdowns.map((b) =>
      b
        ? b.spotPrice +
          b.spotCoefficientSurcharge +
          b.providerMarkup +
          b.energyTax +
          b.vat
        : null
    );
    const numericTotals = totals.filter((t): t is number => t !== null);
    const minTotal = numericTotals.length ? Math.min(...numericTotals) : 0;
    const maxTotal = numericTotals.length ? Math.max(...numericTotals) : 0;
    const minIndex = totals.indexOf(minTotal);
    const maxIndex = totals.indexOf(maxTotal);
    // Highlight the cheapest and most expensive ~5% of slots (at least one each).
    // Rank the real slots that have data by total price, then take the bottom and
    // top slices; the shaded columns are drawn by priceExtremesHighlightPlugin.
    const highlightCount = Math.max(
      1,
      Math.ceil(numericTotals.length * 0.05)
    );
    const rankedByTotal = timeSlots
      .map((_, index) => index)
      .filter((index) => totals[index] !== null)
      .sort((a, b) => (totals[a] as number) - (totals[b] as number));
    const lowHighlightIndices = rankedByTotal.slice(0, highlightCount);
    const highHighlightIndices = rankedByTotal.slice(-highlightCount);
    // Extend the last slot's price to the end of the day: append a closing
    // "24:00" boundary that repeats the final slot's value so the stepped line
    // runs all the way to midnight instead of stopping one interval short. It
    // carries no breakdown, so it stays out of tooltips (see tooltip.filter)
    // and is added after min/max are computed so it can't skew those.
    times.push("24:00");
    breakdowns.push(null);
    totals.push(totals[totals.length - 1] ?? null);
    // Border color the tooltip card gets when hovering a special slot; null = no
    // highlight. Any slot in the highlighted low/high 5% bands gets the matching
    // border, so hovering a shaded area is reinforced in the tooltip too.
    const highlightForIndex = (index: number): string | null => {
      if (index === currentSlotIndex) return nuColor;
      if (highHighlightIndices.includes(index)) return highColor;
      if (lowHighlightIndices.includes(index)) return lowColor;
      return null;
    };

    const currentSlotHighlightPlugin: Plugin<keyof ChartTypeRegistry> = {
      id: "currentSlotHighlight",
      beforeDatasetsDraw(chart) {
        const { currentSlotIndex, totalsLength, highlightColor } =
          drawStateRef.current;
        if (currentSlotIndex < 0) return;
        const xScale = chart.scales.x;
        const { top, bottom, right } = chart.chartArea;
        const slotLeft = xScale.getPixelForValue(currentSlotIndex);
        const slotRight =
          currentSlotIndex === totalsLength - 1
            ? right
            : xScale.getPixelForValue(currentSlotIndex + 1);

        const ctx = chart.ctx;
        ctx.save();
        ctx.fillStyle = highlightColor;
        ctx.fillRect(slotLeft, top, slotRight - slotLeft, bottom - top);
        ctx.restore();
      },
    };
    const currentTimeLinePlugin: Plugin<keyof ChartTypeRegistry> = {
      id: "currentTimeLine",
      afterDatasetsDraw(chart) {
        const { currentSlotIndex, destructiveColor, currentTimeLabel } =
          drawStateRef.current;
        if (currentSlotIndex < 0) return;
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;
        const x = xScale.getPixelForValue(currentSlotIndex);
        const ctx = chart.ctx;

        ctx.save();
        ctx.strokeStyle = destructiveColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x, yScale.top);
        ctx.lineTo(x, yScale.bottom);
        ctx.stroke();
        ctx.fillStyle = destructiveColor;
        ctx.font = "bold 12px sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "bottom";
        ctx.fillText(currentTimeLabel, x + 4, yScale.top + 14);
        ctx.restore();
      },
    };

    // Highlight the cheapest and most expensive ~5% of slots with a faint shaded
    // column behind the line (green = low, red = high), similar to how the "Nu"
    // slot is highlighted. The min/max price values are drawn by totalLabelPlugin.
    const priceExtremesHighlightPlugin: Plugin<keyof ChartTypeRegistry> = {
      id: "priceExtremesHighlight",
      beforeDatasetsDraw(chart) {
        const { lowHighlightIndices, highHighlightIndices, totalsLength, lowColor, highColor } =
          drawStateRef.current;
        const xScale = chart.scales.x;
        const { top, bottom, right } = chart.chartArea;
        const ctx = chart.ctx;

        const shadeSlot = (index: number, color: string) => {
          if (index < 0) return;
          const slotLeft = xScale.getPixelForValue(index);
          const slotRight =
            index >= totalsLength - 1
              ? right
              : xScale.getPixelForValue(index + 1);
          ctx.save();
          ctx.globalAlpha = 0.15;
          ctx.fillStyle = color;
          ctx.fillRect(slotLeft, top, slotRight - slotLeft, bottom - top);
          ctx.restore();
        };

        for (const index of lowHighlightIndices) shadeSlot(index, lowColor);
        for (const index of highHighlightIndices) shadeSlot(index, highColor);
      },
    };

    const totalLabelPlugin: Plugin<keyof ChartTypeRegistry> = {
      id: "totalLabel",
      afterDatasetsDraw(chart) {
        const { minIndex, maxIndex, minTotal, maxTotal, mutedFgColor, highColor, lowColor } =
          drawStateRef.current;
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;
        const ctx = chart.ctx;

        if (minIndex < 0 || maxIndex < 0) return;
        ctx.save();
        ctx.fillStyle = mutedFgColor;
        ctx.font = "600 11px sans-serif";
        ctx.textAlign = "center";

        if (minIndex === maxIndex) {
          const x = xScale.getPixelForValue(minIndex);
          const y = yScale.getPixelForValue(minTotal);
          ctx.textBaseline = "bottom";
          ctx.fillText(formatEuro(minTotal, 4), x, y - 4);
        } else {
          const maxX = xScale.getPixelForValue(maxIndex);
          const maxY = yScale.getPixelForValue(maxTotal);
          const maxLabelY = Math.max(yScale.top + 2, maxY - 6);
          ctx.textBaseline = "bottom";
          ctx.fillStyle = highColor; // red: highest price
          ctx.fillText(formatEuro(maxTotal, 4), maxX, maxLabelY);

          const minX = xScale.getPixelForValue(minIndex);
          const minY = yScale.getPixelForValue(minTotal);
          const minLabelY = Math.min(yScale.bottom + 12, minY + 8);
          ctx.textBaseline = "top";
          ctx.fillStyle = lowColor; // green: lowest price
          ctx.fillText(formatEuro(minTotal, 4), minX, minLabelY);
        }

        ctx.restore();
      },
    };

    const options: ChartOptions<"line"> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: false,
        },
        legend: {
          display: false,
        },
        tooltip: {
          mode: "index",
          intersect: false,
          // The trailing midnight boundary point carries no breakdown; keep it
          // out of the tooltip so hovering the right edge shows nothing extra.
          filter: (item) => breakdowns[item.dataIndex] != null,
          // Highlight the tooltip card border when hovering the Nu, highest or lowest slot.
          borderWidth: (ctx: ScriptableTooltipContext<"line">) =>
            highlightForIndex(ctx.tooltip.dataPoints?.[0]?.dataIndex ?? -1)
              ? 2
              : 0,
          borderColor: (ctx: ScriptableTooltipContext<"line">) =>
            highlightForIndex(ctx.tooltip.dataPoints?.[0]?.dataIndex ?? -1) ??
            "rgba(0, 0, 0, 0)",
          callbacks: {
            label: (context) => {
              const index = context.dataIndex;
              const breakdown = breakdowns[index];
              if (!breakdown) return [];
              const coefficient = providerSpotCoefficient ?? 1;
              return [
                `${STRINGS.chart.energyPrice}: ${formatEuro(breakdown.spotPrice, 4)}/kWh`,
                ...(coefficient !== 1
                  ? [
                      `${STRINGS.chart.spotFactor(
                        formatCoefficient(coefficient)
                      )}: ${formatEuro(
                        breakdown.spotCoefficientSurcharge,
                        4
                      )}/kWh`,
                    ]
                  : []),
                `${STRINGS.chart.supplierMarkup}: ${formatEuro(
                  breakdown.providerMarkup,
                  4
                )}/kWh`,
                `${COUNTRY_CONFIG.energyTaxLabel}: ${formatEuro(breakdown.energyTax, 4)}/kWh`,
                `${STRINGS.chart.vat}: ${formatEuro(breakdown.vat, 4)}/kWh`,
                `${STRINGS.chart.total}: ${formatEuro(totals[index] ?? 0, 4)}/kWh`,
              ];
            },
          },
        },
      },
      layout: {
        padding: {
          top: 12,
          bottom: 20,
        },
      },
      scales: {
        x: {
          // For the 15-minute view, keep only on-the-hour labels (00:00, 01:00,
          // …) instead of one per quarter-hour. Filtering here—before Chart.js'
          // autoSkip runs—lets the remaining hour labels thin and rotate on
          // narrow screens exactly like the hourly chart does.
          afterBuildTicks: (axis) => {
            if (interval !== "15m") return;
            axis.ticks = axis.ticks.filter((tick) => {
              const label = times[tick.value];
              return typeof label === "string" && label.endsWith(":00");
            });
          },
          ticks: {
            autoSkip: true,
            font: {
              size: 12,
            },
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: STRINGS.chart.yAxis,
          },
          ticks: {
            callback: (value) => formatEuro(Number(value), 2),
            font: {
              size: 11,
            },
          },
        },
      },
    };

    const chartData = {
      labels: times,
      datasets: [
        {
          label: STRINGS.chart.total,
          data: totals.map((total) =>
            total === null ? null : Number(total.toFixed(4))
          ),
          borderColor: chartLineColor,
          backgroundColor: chartLineColor,
          stepped: true,
          tension: 0,
          pointRadius: 0,
          pointHitRadius: 8,
        },
      ],
    };

    // Keep the plugin draw-state current so the already-attached plugins draw
    // the "Nu" line and min/max labels from this render's data, not the data
    // captured when the chart was first created.
    drawStateRef.current = {
      currentSlotIndex,
      totalsLength: totals.length,
      minIndex: numericTotals.length ? minIndex : -1,
      maxIndex: numericTotals.length ? maxIndex : -1,
      minTotal,
      maxTotal,
      lowHighlightIndices,
      highHighlightIndices,
      currentTimeLabel,
      highlightColor,
      destructiveColor,
      mutedFgColor,
      highColor,
      lowColor,
      nuColor,
    };

    if (!chartInstance.current) {
      chartInstance.current = new Chart(canvasRef.current, {
        type: "line",
        data: chartData,
        options,
        plugins: [
          currentSlotHighlightPlugin,
          priceExtremesHighlightPlugin,
          currentTimeLinePlugin,
          totalLabelPlugin,
        ],
      });
    } else {
      chartInstance.current.data = chartData;
      chartInstance.current.options = options;
      chartInstance.current.update("none");
    }
  }, [data, interval, providerPerKwhCharge, providerSpotCoefficient, includeTaxes, currentTimeSlot, currentTimeLabel, energyTaxRate]);

  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  return (
    <div className="my-6 mx-0 min-w-0 sm:mx-6">
      <div className="relative pb-3" style={{ height: "350px" }}>
        <canvas ref={canvasRef} />
      </div>
      <ul className="mt-1 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <li className="flex items-center gap-1.5">
          <span
            className="h-3 w-3 rounded-sm border border-positive"
            style={{
              backgroundColor:
                "color-mix(in srgb, var(--positive) 15%, var(--card))",
            }}
            aria-hidden="true"
          />
          {STRINGS.chart.lowestBand}
        </li>
        <li className="flex items-center gap-1.5">
          <span
            className="h-3 w-3 rounded-sm border border-destructive"
            style={{
              backgroundColor:
                "color-mix(in srgb, var(--destructive) 15%, var(--card))",
            }}
            aria-hidden="true"
          />
          {STRINGS.chart.highestBand}
        </li>
      </ul>
    </div>
  );
}

export function PriceChartSkeleton() {
  return (
    <div
      className="my-6 mx-0 min-w-0 sm:mx-6"
      aria-hidden="true"
      data-testid="price-chart-skeleton"
    >
      <div className="pb-3" style={{ height: "350px" }}>
        <Skeleton className="h-full w-full" />
      </div>
      <div className="mt-1 flex justify-center gap-x-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-32" />
      </div>
    </div>
  );
}
